// ViewAsSection (admin/role-based-access §5) — the platform-admin "View as" control.
//
// Renders for nobody but a real platform admin, so this is purely additive — every other user's
// Appearance tab is unchanged.
//
// Gated on `isRealPlatformAdmin` (UNMASKED) rather than <Gate access="platform">: the View-as mask
// removes `admin:approve` while previewing, so a masked gate would hide this section the instant a
// preview started — taking away the control needed to end it.
//
// Since the top banner was removed (2026-09-10, operator request) this section is the ONLY place
// the preview state is shown and the only way to leave it. If a preview lands you on a gated bare
// 404 (no app shell), the route back is that page's "Go to Start processing" link, then Settings →
// Appearance. A preview also ends by itself when the tab closes — it lives in sessionStorage.
//
// Reuses the existing `billing-section` / `theme-card` shells from profile.css rather than
// introducing new visual language for one control.
import { useUiStore } from '@/store/uiStore';
import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/components/ui';
import { TIER_LABEL, type Tier } from '@/auth/tiers';
import '@/styles/pages/profile.css';

/** Offered previews, plus the way back. Platform admins already see their own view. */
const PREVIEW_TIERS: Tier[] = ['org', 'user'];

const TIER_BLURB: Record<Tier, string> = {
  platform: 'Your own view — every page and control you are entitled to.',
  org: 'What an organization owner or admin sees: their org’s pages, without the platform admin surface.',
  user: 'What an ordinary member sees: no org administration and no platform admin surface.',
};

export default function ViewAsSection() {
  const viewAsTier = useUiStore((s) => s.viewAsTier);
  const setViewAsTier = useUiStore((s) => s.setViewAsTier);
  const { previewTier, isRealPlatformAdmin } = usePermissions();

  if (!isRealPlatformAdmin) return null;

  return (
    <div className="billing-section settings-appearance-section">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="billing-title mb-0 border-0 pb-0">View as</h3>
        <Badge tone={previewTier ? 'warning' : 'info'}>
          {previewTier ? `Previewing: ${TIER_LABEL[previewTier]}` : 'Your own view'}
        </Badge>
      </div>
      <p className="text-muted small mb-2">
        Preview the app as a lower tier sees it, to check what is hidden from whom. Navigation,
        pages and controls are gated as that tier would experience them.
      </p>
      <p className="text-muted small mb-4">
        <strong>This is a preview, not impersonation.</strong> The backend still sees you as a
        platform admin, so responses still carry admin-scoped data — pages that read a field off the
        server rather than checking a permission may still show more than the tier really gets. The
        preview ends when you close the tab, or when you pick Platform admin below.
      </p>

      <div className="row g-3">
        {PREVIEW_TIERS.map((tier) => {
          const isActive = viewAsTier === tier;
          return (
            <div key={tier} className="col-md-4">
              <button
                type="button"
                className={`theme-card ${isActive ? 'is-active' : ''}`}
                onClick={() => setViewAsTier(isActive ? null : tier)}
                aria-pressed={isActive}
              >
                <div className="theme-card__info">
                  <span className="theme-card__label">{TIER_LABEL[tier]}</span>
                  {isActive && (
                    <i className="bi bi-check-circle-fill theme-card__check" aria-hidden="true" />
                  )}
                </div>
                <span className="text-muted small d-block text-start">{TIER_BLURB[tier]}</span>
              </button>
            </div>
          );
        })}
        <div className="col-md-4">
          <button
            type="button"
            className={`theme-card ${viewAsTier === null ? 'is-active' : ''}`}
            onClick={() => setViewAsTier(null)}
            aria-pressed={viewAsTier === null}
          >
            <div className="theme-card__info">
              <span className="theme-card__label">{TIER_LABEL.platform}</span>
              {viewAsTier === null && (
                <i className="bi bi-check-circle-fill theme-card__check" aria-hidden="true" />
              )}
            </div>
            <span className="text-muted small d-block text-start">{TIER_BLURB.platform}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
